import React, { useEffect, useState, useMemo } from "react";
import {
  Search,
  Table2,
  ChevronLeft,
  ChevronRight,
  Edit2,
  X,
  Check,
  PlusCircle,
} from "lucide-react";
import axios from "axios";
import Apiurl from "../../environment/Apiurl";

function StockPharmacy({ toggleSidebar }) {
  const [responseData, setResponseData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showEntries, setShowEntries] = useState("10");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingPrivileges, setLoadingPrivileges] = useState(true);
  const [userPrivileges, setUserPrivileges] = useState({});
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [updateId, setUpdateId] = useState(null);
  const [loadingUpdate, setLoadingUpdate] = useState(false);
  const [formData, setFormData] = useState({
    medicine_id: "",
    medicine_name: "",
    category: "",
    batch_no: "",
    expiry_date: "",
    unit_pack: "",
    gst_percentage: "",
    inward_quantity: "",
    outward_quantity: "",
    purchase_price: "",
    mrp: "",
  });
  const [addData, setAddData] = useState({
    medicine_name: "",
    category: "",
    hsn_code: "",
    batch_no: "",
    expiry_date: "",
    unit_pack: "",
    gst_percentage: "",
    inward_quantity: "",
    outward_quantity: "",
    purchase_price: "",
    mrp: "",
  });
  const [editingId, setEditingId] = useState(null);
  const [editFormData, setEditFormData] = useState({
    inward_quantity: "",
    outward_quantity: "",
  });
  const [filterType, setFilterType] = useState("All");

  // Fetch user privileges based on role_name
  const fetchUserPrivileges = async () => {
    try {
      setLoadingPrivileges(true);
      const userRole = localStorage.getItem("role_name") || "unknown";
      console.log("Fetching privileges for role_name:", userRole);

      if (userRole === "unknown") {
        console.warn("No role_name found in localStorage");
        setError(
          "No role assigned to the user. Please contact the administrator."
        );
        setTimeout(() => setError(null), 3000);
        return;
      }

      const response = await axios.get(`${Apiurl}/roles_and_privillage`);
      console.log(
        "Fetched roles data:",
        JSON.stringify(response.data, null, 2)
      );

      if (!response.data || !Array.isArray(response.data)) {
        throw new Error(
          `Invalid or empty roles data received: ${JSON.stringify(
            response.data
          )}`
        );
      }

      const roleData = response.data.find(
        (role) => role.role_name === userRole
      );

      if (roleData) {
        console.log(
          "Found role data for role_name:",
          userRole,
          JSON.stringify(roleData, null, 2)
        );
        const privileges = {};
        roleData.pages.forEach((page) => {
          const pageName = page.page;
          const pagePrivileges = page.priviallages || page.privileges || {};
          privileges[pageName] = {
            access: page.access,
            ...pagePrivileges,
          };
          console.log(
            `Processing page: ${pageName}, Access: ${page.access}, Privileges:`,
            JSON.stringify(pagePrivileges, null, 2)
          );
        });
        console.log(
          "Set user privileges:",
          JSON.stringify(privileges, null, 2)
        );
        setUserPrivileges(privileges);
      } else {
        console.warn("No role data found for role_name:", userRole);
        setError("No role data found for the current user.");
        setTimeout(() => setError(null), 3000);
      }
    } catch (error) {
      console.error(
        "Error fetching user privileges:",
        error.message,
        error.response
          ? JSON.stringify(error.response.data, null, 2)
          : "No response data"
      );
      setError("Failed to fetch user privileges. Please try again.");
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoadingPrivileges(false);
    }
  };

  // Check if user has specific permission for Stock Management page
  const hasPermission = (permission) => {
    const stockPrivileges = userPrivileges["Stock Management"];
    if (!stockPrivileges) {
      console.log("No privileges found for Stock Management page");
      return false;
    }
    console.log(
      `Checking permission '${permission}' for Stock Management:`,
      JSON.stringify(stockPrivileges, null, 2)
    );
    if (permission === "access") {
      const accessGranted = stockPrivileges.access === true;
      console.log(`Access permission result: ${accessGranted}`);
      return accessGranted;
    }
    const hasPerm =
      stockPrivileges.all === true || stockPrivileges[permission] === true;
    console.log(`Permission '${permission}' result: ${hasPerm}`);
    return hasPerm;
  };

  const fetchData = async () => {
    if (!hasPermission("read")) {
      console.warn("Read permission denied for Stock Management page");
      setError("You do not have permission to view stock records.");
      setTimeout(() => setError(null), 3000);
      return;
    }

    setLoading(true);
    try {
      console.log("Fetching stock data from:", `${Apiurl}/stock_pharmacy`);
      const response = await axios.get(`${Apiurl}/stock_pharmacy`);

      // Sort data by 'created_at' so newest appears first
      const sortedData = (response.data || []).sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );

      console.log("Fetched stock data:", JSON.stringify(sortedData, null, 2));
      setResponseData(sortedData);
      setError(null);
    } catch (error) {
      console.error(
        "Error fetching stock data:",
        error.message,
        error.response
          ? JSON.stringify(error.response.data, null, 2)
          : "No response data"
      );
      setError("Failed to fetch stock data. Please try again.");
      setTimeout(() => setError(null), 3000);
      setResponseData([]);
    } finally {
      setLoading(false);
    }
  };

  // Initialize privileges and data
  useEffect(() => {
    console.log("Fetching initial privileges...");
    fetchUserPrivileges();
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    if (!loadingPrivileges && hasPermission("access")) {
      console.log("Access granted for Stock Management page");
      if (hasPermission("read")) {
        console.log("Fetching stock data due to read permission");
        fetchData();
      } else {
        console.warn("Read permission denied for Stock Management page");
        setError("You do not have permission to view stock records.");
        setTimeout(() => setError(null), 3000);
      }
    } else if (!loadingPrivileges) {
      console.warn("Access permission denied for Stock Management page");
      setError("You do not have permission to access this page.");
      setTimeout(() => setError(null), 3000);
    }
  }, [userPrivileges, loadingPrivileges]);

  // Filter data based on search term and category
  const filteredData = useMemo(() => {
    let result = responseData.filter((item) =>
      [
        item.medicine_name,
        item.medicine_details?.medicine_name,
        item.batch_no,
        item.expiry_date,
        item.unit_pack,
        item.gst_percentage?.toString(),
        item.inward_quantity?.toString(),
        item.outward_quantity?.toString(),
        item.balance_quantity?.toString(),
        item.purchase_price?.toString(),
        item.category,
        item.mrp?.toString(),
      ].some((prop) =>
        prop != null
          ? prop.toString().toLowerCase().includes(searchTerm.toLowerCase())
          : false
      )
    );

    if (filterType !== "All") {
      result = result.filter(
        (item) =>
          (item.category || item.medicine_details?.category) === filterType
      );
    }

    console.log("Filtered stock data:", result);
    return result;
  }, [responseData, searchTerm, filterType]);

  // Update items per page
  useEffect(() => {
    console.log("Updating items per page to:", showEntries);
    if (showEntries === "All") {
      setItemsPerPage(filteredData.length || 1);
    } else {
      setItemsPerPage(Number(showEntries));
    }
  }, [showEntries, filteredData]);

  // Reset to first page when search term, items per page, or filter type changes
  useEffect(() => {
    console.log(
      "Resetting current page due to searchTerm, itemsPerPage, or filterType change"
    );
    setCurrentPage(1);
  }, [searchTerm, itemsPerPage, filterType]);

  // Calculate pagination values
  const totalPages = Math.ceil(filteredData.length / itemsPerPage) || 1;
  console.log(
    "Pagination - totalPages:",
    totalPages,
    "itemsPerPage:",
    itemsPerPage,
    "filteredData.length:",
    filteredData.length
  );
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
  console.log(
    "Current items:",
    currentItems,
    "from index",
    indexOfFirstItem,
    "to",
    indexOfLastItem
  );

  // Generate page list for pagination
  const getPageList = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 4) {
        pages.push(1, 2, 3, 4, 5, "...", totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(
          1,
          "...",
          totalPages - 4,
          totalPages - 3,
          totalPages - 2,
          totalPages - 1,
          totalPages
        );
      } else {
        pages.push(
          1,
          "...",
          currentPage - 1,
          currentPage,
          currentPage + 1,
          "...",
          totalPages
        );
      }
    }
    console.log("Generated page list:", pages);
    return pages;
  };

  const pages = getPageList();

  const handlePageClick = (page) => {
    if (page !== "..." && page !== currentPage) {
      console.log("Navigating to page:", page);
      setCurrentPage(page);
    }
  };

  const handleResetFilter = () => setFilterType("All");
  const handleHomeoFilter = () => setFilterType("Homeopathy");
  const handleYogaFilter = () => setFilterType("Yoga");

  const openUpdateModal = (item) => {
    if (!hasPermission("update")) {
      console.warn("Update permission denied for Stock Management page");
      setError("You do not have permission to update stock records.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    if (!item._id) {
      console.warn("Invalid stock record ID:", item._id);
      setError("Invalid stock record ID.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    console.log("Opening Update Stock modal for ID:", item._id);
    setUpdateId(item._id);
    setFormData({
      medicine_id: item.medicine_id || "",
      medicine_name:
        item.medicine_name || item.medicine_details?.medicine_name || "N/A",
      category: item.category || item.medicine_details?.category || "N/A",
      batch_no: item.batch_no || "",
      expiry_date: item.expiry_date || "",
      unit_pack: item.unit_pack || "",
      gst_percentage:
        item.gst_percentage != null ? item.gst_percentage.toString() : "",
      inward_quantity:
        item.inward_quantity != null ? item.inward_quantity.toString() : "",
      outward_quantity:
        item.outward_quantity != null ? item.outward_quantity.toString() : "",
      purchase_price:
        item.purchase_price != null ? item.purchase_price.toString() : "",
      mrp: item.mrp != null ? item.mrp.toString() : "",
    });
    setShowUpdateModal(true);
  };

  const closeUpdateModal = () => {
    console.log("Closing Update Stock modal");
    setShowUpdateModal(false);
    setUpdateId(null);
    setFormData({
      medicine_id: "",
      medicine_name: "",
      category: "",
      batch_no: "",
      expiry_date: "",
      unit_pack: "",
      gst_percentage: "",
      inward_quantity: "",
      outward_quantity: "",
      purchase_price: "",
      mrp: "",
    });
    setError(null);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    console.log(`Form field ${name} changed to:`, value);
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddChange = (e) => {
    const { name, value } = e.target;
    console.log(`Add form field ${name} changed to:`, value);
    setAddData((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    const {
      batch_no,
      expiry_date,
      unit_pack,
      gst_percentage,
      inward_quantity,
      outward_quantity,
      purchase_price,
      mrp,
    } = formData;

    if (!batch_no || !expiry_date || !unit_pack) {
      return "Batch No, Expiry Date, and Unit Pack are required.";
    }
    if (isNaN(gst_percentage) || Number(gst_percentage) < 0) {
      return "GST Percentage must be a non-negative number.";
    }
    if (isNaN(inward_quantity) || Number(inward_quantity) < 0) {
      return "Inward Quantity must be a non-negative number.";
    }
    if (isNaN(outward_quantity) || Number(outward_quantity) < 0) {
      return "Outward Quantity must be a non-negative number.";
    }
    if (Number(inward_quantity) < Number(outward_quantity)) {
      return "Outward Quantity cannot exceed Inward Quantity.";
    }
    if (isNaN(purchase_price) || Number(purchase_price) < 0) {
      return "Purchase Price must be a non-negative number.";
    }
    if (isNaN(mrp) || Number(mrp) < 0) {
      return "MRP must be a non-negative number.";
    }
    return null;
  };

  const validateAddForm = () => {
    const {
      medicine_name,
      category,
      batch_no,
      expiry_date,
      unit_pack,
      gst_percentage,
      inward_quantity,
      outward_quantity,
      purchase_price,
      mrp,
    } = addData;

    if (
      !medicine_name ||
      !category ||
      !batch_no ||
      !expiry_date ||
      !unit_pack
    ) {
      return "Medicine Name, Category, Batch No, Expiry Date, and Unit Pack are required.";
    }
    if (isNaN(gst_percentage) || Number(gst_percentage) < 0) {
      return "GST Percentage must be a non-negative number.";
    }
    if (isNaN(inward_quantity) || Number(inward_quantity) < 0) {
      return "Inward Quantity must be a non-negative number.";
    }
    if (isNaN(outward_quantity) || Number(outward_quantity) < 0) {
      return "Outward Quantity must be a non-negative number.";
    }
    if (Number(inward_quantity) < Number(outward_quantity)) {
      return "Outward Quantity cannot exceed Inward Quantity.";
    }
    if (isNaN(purchase_price) || Number(purchase_price) < 0) {
      return "Purchase Price must be a non-negative number.";
    }
    if (isNaN(mrp) || Number(mrp) < 0) {
      return "MRP must be a non-negative number.";
    }
    return null;
  };

  const confirmUpdate = async () => {
    if (!hasPermission("update")) {
      console.warn("Update permission denied for Stock Management page");
      setError("You do not have permission to update stock records.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    if (!updateId) {
      console.warn("Invalid stock record ID:", updateId);
      setError("Invalid stock record ID.");
      setTimeout(() => setError(null), 3000);
      return;
    }

    const validationError = validateForm();
    if (validationError) {
      console.warn("Form validation failed:", validationError);
      setError(validationError);
      setTimeout(() => setError(null), 3000);
      return;
    }

    setLoadingUpdate(true);
    try {
      console.log(
        "Updating stock with ID:",
        updateId,
        "Data:",
        JSON.stringify(formData, null, 2)
      );
      const response = await axios.put(`${Apiurl}/stock_pharmacy/${updateId}`, {
        medicine_name: formData.medicine_name,
        category: formData.category,
        batch_no: formData.batch_no,
        expiry_date: formData.expiry_date,
        unit_pack: formData.unit_pack,
        gst_percentage: Number(formData.gst_percentage),
        inward_quantity: Number(formData.inward_quantity),
        outward_quantity: Number(formData.outward_quantity),
        balance_quantity:
          Number(formData.inward_quantity) - Number(formData.outward_quantity),
        purchase_price: Number(formData.purchase_price),
        mrp: Number(formData.mrp),
      });
      console.log(
        "Stock updated successfully:",
        JSON.stringify(response.data, null, 2)
      );
      fetchData();
      closeUpdateModal();
    } catch (error) {
      console.error(
        "Error updating stock:",
        error.message,
        error.response
          ? JSON.stringify(error.response.data, null, 2)
          : "No response data"
      );
      setError(
        error.response?.data?.message ||
          "Failed to update stock. Please try again."
      );
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoadingUpdate(false);
    }
  };

  const addStock = () => {
    if (!hasPermission("create")) {
      console.warn("Create permission denied for Stock Management page");
      setError("You do not have permission to add stock records.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    console.log("Opening Add Stock modal");
    setAddData({
      medicine_name: "",
      category: "",
      hsn_code: "",
      batch_no: "",
      expiry_date: "",
      unit_pack: "",
      gst_percentage: "",
      inward_quantity: "",
      outward_quantity: "",
      purchase_price: "",
      mrp: "",
    });
    setShowAddModal(true);
  };

  const closeAddModal = () => {
    console.log("Closing Add Stock modal");
    setShowAddModal(false);
    setAddData({
      medicine_name: "",
      category: "",
      hsn_code: "",
      batch_no: "",
      expiry_date: "",
      unit_pack: "",
      gst_percentage: "",
      inward_quantity: "",
      outward_quantity: "",
      purchase_price: "",
      mrp: "",
    });
    setError(null);
  };

  const addStockRecord = async () => {
    if (!hasPermission("create")) {
      console.warn("Create permission denied for Stock Management page");
      setError("You do not have permission to add stock records.");
      setTimeout(() => setError(null), 3000);
      return;
    }

    const validationError = validateAddForm();
    if (validationError) {
      console.warn("Form validation failed:", validationError);
      setError(validationError);
      setTimeout(() => setError(null), 3000);
      return;
    }

    setLoadingUpdate(true);
    try {
      console.log(
        "Adding new stock with data:",
        JSON.stringify(addData, null, 2)
      );
      const response = await axios.post(`${Apiurl}/stock_pharmacy`, {
        medicine_name: addData.medicine_name,
        category: addData.category,
        hsn_code: addData.hsn_code,
        batch_no: addData.batch_no,
        expiry_date: addData.expiry_date,
        unit_pack: addData.unit_pack,
        gst_percentage: Number(addData.gst_percentage),
        inward_quantity: Number(addData.inward_quantity),
        outward_quantity: Number(addData.outward_quantity),
        purchase_price: Number(addData.purchase_price),
        mrp: Number(addData.mrp),
      });

      console.log(
        "Stock added successfully:",
        JSON.stringify(response.data, null, 2)
      );
      fetchData();
      closeAddModal();
    } catch (error) {
      console.error(
        "Error adding stock:",
        error.message,
        error.response
          ? JSON.stringify(error.response.data, null, 2)
          : "No response data"
      );
      setError(
        error.response?.data?.message ||
          "Failed to add stock. Please try again."
      );
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoadingUpdate(false);
    }
  };

  const startEditing = (item) => {
    if (!hasPermission("update")) {
      setError("You do not have permission to update stock records.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    setEditingId(item._id);
    setEditFormData({
      inward_quantity:
        item.inward_quantity != null ? item.inward_quantity.toString() : "0",
      outward_quantity:
        item.outward_quantity != null ? item.outward_quantity.toString() : "0",
    });
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({ ...prev, [name]: value }));
  };

  const saveEdit = async (id) => {
    const inward = Number(editFormData.inward_quantity);
    const outward = Number(editFormData.outward_quantity);
    const balance = inward - outward;

    if (isNaN(inward) || inward < 0) {
      setError("Inward Quantity must be a non-negative number.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    if (isNaN(outward) || outward < 0) {
      setError("Outward Quantity must be a non-negative number.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    if (balance < 0) {
      setError("Balance Quantity cannot be negative.");
      setTimeout(() => setError(null), 3000);
      return;
    }

    setLoading(true);
    try {
      const item = responseData.find((data) => data._id === id);
      if (!item) {
        setError("Stock item not found.");
        setTimeout(() => setError(null), 3000);
        return;
      }
      const response = await axios.put(`${Apiurl}/stock_pharmacy/${id}`, {
        medicine_name:
          item.medicine_name || item.medicine_details?.medicine_name || "N/A",
        category: item.category || item.medicine_details?.category || "N/A",
        inward_quantity: inward,
        outward_quantity: outward,
        balance_quantity: balance,
      });
      setResponseData((prev) =>
        prev.map((item) =>
          item._id === id
            ? {
                ...item,
                inward_quantity: inward,
                outward_quantity: outward,
                balance_quantity: balance,
              }
            : item
        )
      );
      setEditingId(null);
    } catch (error) {
      console.error(
        "Error updating quantities:",
        error.message,
        error.response
          ? JSON.stringify(error.response.data, null, 2)
          : "No response data"
      );
      setError(
        error.response?.data?.message ||
          "Failed to update quantities. Please try again."
      );
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditFormData({});
  };

  if (loadingPrivileges) {
    return <div className="p-3 text-center">Loading permissions...</div>;
  }

  if (!hasPermission("access")) {
    console.log(
      "Access denied for role_name:",
      localStorage.getItem("role_name"),
      "on Stock Management page"
    );
    return (
      <div className="p-3">
        <div className="alert alert-danger">
          You don&apos;t have permission to access this page.
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-3 px-2">
      <style>{`
        .btn-custom {
          background-color: #01AEBE;
          color: white;
          border: none;
        }
        .btn-custom:hover {
          background-color: #018A97;
          color: white;
        }
        .form-label {
          font-size: 0.9rem;
          margin-bottom: 0.2rem;
        }
        .form-control-sm {
          font-size: 0.875rem;
          padding: 0.25rem 0.5rem;
        }
      `}</style>

      {error && (
        <div className="alert alert-danger alert-dismissible fade show mt-2">
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError(null)}
          ></button>
        </div>
      )}

      {hasPermission("read") && (
        <div className="card card-body mt-2">
          <div className="row mb-2 crm-table-toolbar">
            <div className="col-md-2">
              <div className="input-group">
                <span className="input-group-text">
                  <Table2 size={16} />
                </span>
                <select
                  className="form-select"
                  value={showEntries}
                  onChange={(e) => {
                    console.log(
                      "Changing entries per page to:",
                      e.target.value
                    );
                    setShowEntries(e.target.value);
                  }}
                >
                  <option value="10">Show 10</option>
                  <option value="20">Show 20</option>
                  <option value="50">Show 50</option>
                  <option value="100">Show 100</option>
                  <option value="200">Show 200</option>
                  <option value="500">Show 500</option>
                  <option value="1000">Show 1000</option>
                  <option value="All">Show All</option>
                </select>
              </div>
            </div>
            <div className="col-md-2"></div>
            <div className="col-md-3">
              <div className="rounded-5">
                <div className="btn-group crm-filter-tabs" role="group">
                  <button
                    className={`btn btn-sm ${
                      filterType === "All"
                        ? "btn-success"
                        : "btn-outline-success"
                    }`}
                    onClick={handleResetFilter}
                  >
                    All
                  </button>
                  <button
                    className={`btn btn-sm ${
                      filterType === "Homeopathy"
                        ? "btn-primary"
                        : "btn-outline-primary"
                    }`}
                    onClick={handleHomeoFilter}
                  >
                    Homeo
                  </button>
                  <button
                    className={`btn btn-sm ${
                      filterType === "Yoga"
                        ? "btn-secondary"
                        : "btn-outline-secondary"
                    }`}
                    onClick={handleYogaFilter}
                  >
                    Yoga
                  </button>
                </div>
              </div>
            </div>
            <div className="col-md-2">
              <button
                className="form-control btn btn-sm btn-secondary mt-1"
                onClick={addStock}
              >
                Add Stock
              </button>
            </div>
            <div className="col-md-3">
              <div className="d-flex align-items-center">
                <div className="position-relative">
                  <input
                    type="text"
                    className="form-control pe-5"
                    placeholder="Search by medicine, batch, or expiry"
                    value={searchTerm}
                    onChange={(e) => {
                      console.log("Search term changed to:", e.target.value);
                      setSearchTerm(e.target.value);
                    }}
                    style={{ width: "250px", paddingRight: "2.5rem" }}
                  />
                  {searchTerm && (
                    <button
                      className="position-absolute bg-transparent border-0"
                      style={{
                        right: "30px",
                        top: "50%",
                        transform: "translateY(-50%)",
                      }}
                      onClick={() => setSearchTerm("")}
                    >
                      <i className="fas fa-times text-muted"></i>
                    </button>
                  )}
                  <Search
                    size={16}
                    className="position-absolute text-muted"
                    style={{
                      right: "10px",
                      top: "35%",
                      transform: "translateY(-50%)",
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {loading && <div className="text-center py-3">Loading...</div>}

          <div className="table-responsive mt-3">
            <table className="table table-hover">
              <thead style={{ backgroundColor: "#01AEBE", color: "white" }}>
                <tr>
                  <th>S.No</th>
                  <th>Medicine Name</th>
                  <th>Batch No</th>
                  <th>Expiry Date</th>
                  <th>Category</th>
                  <th>InQty</th>
                  <th>OutQty</th>
                  <th>Balance Stock</th>
                  <th>GST</th>
                  <th>MRP</th>
                  {hasPermission("update") && <th>Action</th>}
                </tr>
              </thead>
              <tbody>
                {currentItems.length > 0 ? (
                  currentItems.map((item, index) => (
                    <tr key={item._id || index} className="align-middle">
                      <td>{indexOfFirstItem + index + 1}</td>
                      <td>
                        {item.medicine_name ||
                          item.medicine_details?.medicine_name ||
                          "N/A"}
                      </td>
                      <td>{item.batch_no || "N/A"}</td>
                      <td>{item.expiry_date || "N/A"}</td>
                      <td>
                        {item.category ||
                          item.medicine_details?.category ||
                          "N/A"}
                      </td>
                      <td>
                        {editingId === item._id ? (
                          <input
                            type="number"
                            name="inward_quantity"
                            value={editFormData.inward_quantity}
                            onChange={handleEditChange}
                            className="form-control form-control-sm"
                            min="0"
                          />
                        ) : item.inward_quantity != null ? (
                          item.inward_quantity
                        ) : (
                          "N/A"
                        )}
                      </td>
                      <td>
                        {editingId === item._id ? (
                          <input
                            type="number"
                            name="outward_quantity"
                            value={editFormData.outward_quantity}
                            onChange={handleEditChange}
                            className="form-control form-control-sm"
                            min="0"
                          />
                        ) : item.outward_quantity != null ? (
                          item.outward_quantity
                        ) : (
                          "N/A"
                        )}
                      </td>
                      <td>
                        {editingId === item._id
                          ? Number(editFormData.inward_quantity || 0) -
                            Number(editFormData.outward_quantity || 0)
                          : item.balance_quantity != null
                          ? item.balance_quantity
                          : "N/A"}
                      </td>
                      <td>
                        {item.gst_percentage != null
                          ? `${Number(item.gst_percentage)}%`
                          : "N/A"}
                      </td>

                      <td>
                        {item.mrp != null
                          ? `₹${Number(item.mrp).toFixed(2)}`
                          : "N/A"}
                      </td>
                      {hasPermission("update") && (
                        <td>
                          <button
                            className="btn btn-sm p-1 border-0 me-1"
                            title="Full Update"
                            onClick={() => openUpdateModal(item)}
                            disabled={!item._id || editingId === item._id}
                          >
                            <Edit2 size={16} className="text-primary" />
                          </button>
                          {editingId === item._id ? (
                            <>
                              <button
                                className="btn btn-sm btn-success me-1"
                                onClick={() => saveEdit(item._id)}
                                disabled={loading}
                              >
                                <Check size={12} className="text-white" />
                              </button>
                              <button
                                className="btn btn-sm btn-danger"
                                onClick={cancelEdit}
                              >
                                <X size={12} className="text-white" />
                              </button>
                            </>
                          ) : (
                            <button
                              className="btn btn-sm p-1 border-0"
                              title="Edit Quantities"
                              onClick={() => startEditing(item)}
                              disabled={!item._id}
                            >
                              <PlusCircle size={16} className="text-success" />
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={hasPermission("update") ? 10 : 9}
                      className="text-center py-4"
                    >
                      {loading ? (
                        "Loading..."
                      ) : (
                        <div className="text-muted">
                          <i className="fas fa-search fa-2x mb-2"></i>
                          <br />
                          No stock records found matching your search criteria
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {filteredData.length > 0 && showEntries !== "All" && (
            <div className="container">
              <div className="d-flex justify-content-between align-items-center p-3 border-top gap-2">
                <button
                  className="btn btn-outline-secondary d-flex align-items-center gap-2"
                  onClick={() =>
                    currentPage > 1 && handlePageClick(currentPage - 1)
                  }
                  disabled={currentPage === 1}
                >
                  <ChevronLeft size={16} />
                  <span className="d-none d-sm-inline">Previous</span>
                </button>
                <div
                  className="d-flex overflow-auto flex-nowrap gap-2 px-2"
                  style={{ maxWidth: "100%" }}
                >
                  {pages.map((page, index) => (
                    <button
                      key={index}
                      className={`btn btn-sm px-3 flex-shrink-0 ${
                        page === currentPage
                          ? "text-white"
                          : "btn-outline-secondary"
                      }`}
                      style={
                        page === currentPage
                          ? { backgroundColor: "#01AEBE", border: "none" }
                          : {}
                      }
                      onClick={() => handlePageClick(page)}
                      disabled={page === "..."}
                    >
                      {page}
                    </button>
                  ))}
                </div>
                <button
                  className="btn btn-outline-secondary d-flex align-items-center gap-2"
                  onClick={() =>
                    currentPage < totalPages && handlePageClick(currentPage + 1)
                  }
                  disabled={currentPage === totalPages}
                >
                  <span className="d-none d-sm-inline">Next</span>
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Update Modal */}
      {hasPermission("update") && (
        <div
          className={`modal fade ${showUpdateModal ? "show" : ""}`}
          style={{ display: showUpdateModal ? "block" : "none" }}
          tabIndex="-1"
          aria-labelledby="updateStockModalLabel"
          aria-hidden={!showUpdateModal}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: "10px" }}>
              <div className="modal-header" style={{ borderBottom: "none" }}>
                <h5 className="modal-title" id="updateStockModalLabel">
                  Update Stock
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  style={{ fontSize: "10px" }}
                  onClick={closeUpdateModal}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Medicine Name</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      name="medicine_name"
                      value={formData.medicine_name}
                      readOnly
                      placeholder="Medicine Name"
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Category</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      name="category"
                      value={formData.category}
                      readOnly
                      placeholder="Category"
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Batch No</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      name="batch_no"
                      value={formData.batch_no}
                      onChange={handleFormChange}
                      placeholder="Enter Batch No"
                      required
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Expiry Date (MM/YY)</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      name="expiry_date"
                      value={formData.expiry_date}
                      onChange={handleFormChange}
                      placeholder="MM/YY"
                      required
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Unit Pack</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      name="unit_pack"
                      value={formData.unit_pack}
                      onChange={handleFormChange}
                      placeholder="Enter Unit Pack (e.g., 1*10)"
                      required
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">GST Percentage</label>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      name="gst_percentage"
                      value={formData.gst_percentage}
                      onChange={handleFormChange}
                      placeholder="Enter GST %"
                      min="0"
                      step="0.01"
                      required
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Inward Quantity</label>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      name="inward_quantity"
                      value={formData.inward_quantity}
                      onChange={handleFormChange}
                      placeholder="Enter Inward Quantity"
                      min="0"
                      required
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Outward Quantity</label>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      name="outward_quantity"
                      value={formData.outward_quantity}
                      onChange={handleFormChange}
                      placeholder="Enter Outward Quantity"
                      min="0"
                      required
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Balance Quantity</label>
                    <div className="form-control form-control-sm">
                      {Number(formData.inward_quantity || 0) -
                        Number(formData.outward_quantity || 0)}
                    </div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Purchase Price</label>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      name="purchase_price"
                      value={formData.purchase_price}
                      onChange={handleFormChange}
                      placeholder="Enter Purchase Price"
                      min="0"
                      step="0.01"
                      required
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">MRP</label>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      name="mrp"
                      value={formData.mrp}
                      onChange={handleFormChange}
                      placeholder="Enter MRP"
                      min="0"
                      step="0.01"
                      required
                    />
                  </div>
                </div>
              </div>
              <div
                className="modal-footer"
                style={{ borderTop: "none", justifyContent: "center" }}
              >
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  style={{
                    marginRight: "10px",
                    backgroundColor: "#e0e0e0",
                    color: "#333",
                  }}
                  onClick={closeUpdateModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-custom btn-sm"
                  onClick={confirmUpdate}
                  disabled={loadingUpdate}
                >
                  {loadingUpdate ? "Updating..." : "Update Stock"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Stock Modal */}
      {hasPermission("create") && (
        <div
          className={`modal fade ${showAddModal ? "show" : ""}`}
          style={{ display: showAddModal ? "block" : "none" }}
          tabIndex="-1"
          aria-labelledby="addStockModalLabel"
          aria-hidden={!showAddModal}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: "10px" }}>
              <div className="modal-header" style={{ borderBottom: "none" }}>
                <h5 className="modal-title" id="addStockModalLabel">
                  Add Stock
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  style={{ fontSize: "10px" }}
                  onClick={closeAddModal}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Medicine Name</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      name="medicine_name"
                      value={addData.medicine_name}
                      onChange={handleAddChange}
                      placeholder="Enter medicine"
                      required
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Category</label>
                    <select
                      className="form-control form-control-sm"
                      name="category"
                      value={addData.category}
                      onChange={handleAddChange}
                      required
                    >
                      <option value="">Select category</option>
                      <option value="Yoga">Yoga</option>
                      <option value="Homeopathy">Homeopathy</option>
                    </select>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">HSN Code</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      name="hsn_code"
                      value={addData.hsn_code}
                      onChange={handleAddChange}
                      placeholder="Enter HSN code"
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Batch No</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      name="batch_no"
                      value={addData.batch_no}
                      onChange={handleAddChange}
                      placeholder="Enter Batch No"
                      required
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Expiry Date (MM/YY)</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      name="expiry_date"
                      value={addData.expiry_date}
                      onChange={handleAddChange}
                      placeholder="MM/YY"
                      required
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Unit Pack</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      name="unit_pack"
                      value={addData.unit_pack}
                      onChange={handleAddChange}
                      placeholder="Enter Unit Pack (e.g., 1*10)"
                      required
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">GST Percentage</label>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      name="gst_percentage"
                      value={addData.gst_percentage}
                      onChange={handleAddChange}
                      placeholder="Enter GST %"
                      min="0"
                      step="0.01"
                      required
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Inward Quantity</label>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      name="inward_quantity"
                      value={addData.inward_quantity}
                      onChange={handleAddChange}
                      placeholder="Enter Inward Quantity"
                      min="0"
                      required
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Outward Quantity</label>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      name="outward_quantity"
                      value={addData.outward_quantity}
                      onChange={handleAddChange}
                      placeholder="Enter Outward Quantity"
                      min="0"
                      required
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Balance Quantity</label>
                    <div className="form-control form-control-sm">
                      {Number(addData.inward_quantity || 0) -
                        Number(addData.outward_quantity || 0)}
                    </div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Purchase Price</label>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      name="purchase_price"
                      value={addData.purchase_price}
                      onChange={handleAddChange}
                      placeholder="Enter Purchase Price"
                      min="0"
                      step="0.01"
                      required
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">MRP</label>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      name="mrp"
                      value={addData.mrp}
                      onChange={handleAddChange}
                      placeholder="Enter MRP"
                      min="0"
                      step="0.01"
                      required
                    />
                  </div>
                </div>
              </div>
              <div
                className="modal-footer"
                style={{ borderTop: "none", justifyContent: "center" }}
              >
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  style={{
                    marginRight: "10px",
                    backgroundColor: "#e0e0e0",
                    color: "#333",
                  }}
                  onClick={closeAddModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-custom btn-sm"
                  onClick={addStockRecord}
                  disabled={loadingUpdate}
                >
                  {loadingUpdate ? "Adding..." : "Add Stock"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {(showUpdateModal || showAddModal) && (
        <div className="modal-backdrop fade show"></div>
      )}
    </div>
  );
}

export default StockPharmacy;
